import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router'

import { PortalEntryRedirect } from '@/app/portal-entry-redirect'
import { RootLayout } from '@/app/root-layout'
import { UnknownRouteRedirect } from '@/app/unknown-route-redirect'
import { AdminAppTagsPage } from '@/features/admin/app-tags/app-tags-page'
import { AdminAppReviewPage } from '@/features/admin/apps/review-page'
import { AdminAppsPage } from '@/features/admin/apps/apps-page'
import { AdminModelCallLogsPage } from '@/features/admin/model-call-logs/model-call-logs-page'
import { AdminModelKeysPage } from '@/features/admin/model-keys/model-keys-page'
import { AdminModelProviderPage } from '@/features/admin/model-provider/model-provider-page'
import { AdminOrganizationDepartmentsPage } from '@/features/admin/organization/departments-page'
import { AdminMemberAccountsPage } from '@/features/admin/organization/member-accounts-page'
import { AdminOrganizationSyncSettingsPage } from '@/features/admin/organization/sync-settings-page'
import { AdminProfilePage } from '@/features/admin/profile/profile-page'
import { AdminAccountsPage } from '@/features/admin/settings/accounts-page'
import { AdminAdpCredentialsPage } from '@/features/admin/settings/adp-credentials-page'
import { AdminBrandingSettingsPage } from '@/features/admin/settings/branding-page'
import { AdminBusinessNavigationPage } from '@/features/admin/settings/business-navigation-page'
import { AdminCoreAgentSettingsPage } from '@/features/admin/settings/core-agent-page'
import { AdminOidcClientDetailPage } from '@/features/admin/settings/idp-client-detail-page'
import { AdminOidcClientScopeDetailPage } from '@/features/admin/settings/idp-client-scope-detail-page'
import { AdminOidcClientScopesPage } from '@/features/admin/settings/idp-client-scopes-page'
import { AdminOidcClientsPage } from '@/features/admin/settings/idp-clients-page'
import { AdminOAuthResourcesPage } from '@/features/admin/settings/idp-oauth-resources-page'
import { AdminIdpSettingsPage } from '@/features/admin/settings/idp-page'
import { AdminSsoSettingsPage } from '@/features/admin/settings/sso-page'
import { AdminWecomIdentityProfilesPage } from '@/features/admin/settings/wecom-identity-profiles-page'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { OverviewPage } from '@/features/admin/shell/overview-page'
import { AuthPage } from '@/features/auth/login/portal-login-page'
import { PasswordResetPage } from '@/features/auth/password-reset/password-reset-page'
import { SsoCallbackPage } from '@/features/auth/sso/sso-callback-page'
import { OAuthAuthorizationResumePage } from '@/features/auth/oauth/oauth-authorization-resume-page'
import { WecomAuthPage } from '@/features/auth/wecom/wecom-auth-page'
import { ChatRoutePage } from '@/features/user/chat/chat-route-page'
import { UserProfilePage } from '@/features/user/profile/profile-page'
import { WecomBindingPage } from '@/features/user/wecom-binding/wecom-binding-page'
import { HomePage } from '@/features/user/shell/home-page'
import { UserPlaceholderPage } from '@/features/user/shell/shell-page'
import {
  isPortalPasswordResetRequired,
  portalDefaultPath,
  portalLoginRoutePath,
  portalPasswordResetRoutePath,
  resolveProtectedRouteRedirectPath,
} from '@/lib/auth/portal-routes'
import { getStoredToken, type PortalKind } from '@/lib/auth/token-storage'

function redirectAuthenticatedPortal(portal: PortalKind) {
  if (!getStoredToken(portal)) {
    return
  }

  redirect({
    throw: true,
    to: isPortalPasswordResetRequired(portal)
      ? portalPasswordResetRoutePath[portal]
      : portalDefaultPath[portal],
  })
}

function redirectSettledResetPortal(portal: PortalKind) {
  if (!getStoredToken(portal)) {
    redirect({ throw: true, to: portalLoginRoutePath[portal] })
  }

  if (!isPortalPasswordResetRequired(portal)) {
    redirect({ throw: true, to: portalDefaultPath[portal] })
  }
}

function requirePortalSession(portal: PortalKind) {
  const redirectPath = resolveProtectedRouteRedirectPath(portal)
  if (redirectPath) {
    redirect({ throw: true, to: redirectPath })
  }
}

const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: UnknownRouteRedirect,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => <PortalEntryRedirect portal="user" />,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    redirectAuthenticatedPortal('user')
  },
  component: () => <AuthPage portal="user" />,
})

const passwordLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login/password',
  beforeLoad: () => {
    redirectAuthenticatedPortal('user')
  },
  component: () => <AuthPage loginMode="password" portal="user" />,
})

const ssoCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin/sso',
  component: SsoCallbackPage,
})

const oauthAuthorizationResumeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin/oauth',
  component: OAuthAuthorizationResumePage,
})

const wecomAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin/wecom',
  component: WecomAuthPage,
})

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: () => <PortalEntryRedirect portal="admin" />,
})

const adminLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/login',
  beforeLoad: () => {
    redirectAuthenticatedPortal('admin')
  },
  component: () => <AuthPage portal="admin" />,
})

const userPasswordResetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  beforeLoad: () => {
    redirectSettledResetPortal('user')
  },
  component: () => <PasswordResetPage portal="user" />,
})

const adminPasswordResetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/reset-password',
  beforeLoad: () => {
    redirectSettledResetPortal('admin')
  },
  component: () => <PasswordResetPage portal="admin" />,
})

const appsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/apps',
  beforeLoad: () => {
    requirePortalSession('user')
  },
  component: HomePage,
})

const chatRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat',
  beforeLoad: () => {
    requirePortalSession('user')
  },
  component: ChatRoutePage,
})

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  beforeLoad: () => {
    requirePortalSession('user')
  },
  component: UserProfilePage,
})

const wecomBindingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/im/wecom/bind/$bindingToken',
  component: WecomBindingPage,
})

const userPlaceholderPaths = ['/chat/new'] as const

const userPlaceholderRoutes = userPlaceholderPaths.map((path) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: () => {
      requirePortalSession('user')
    },
    component: () => <UserPlaceholderPage activePath={path} />,
  }),
)

const adminOverviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/overview',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: OverviewPage,
})

const adminProfileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/profile',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminProfilePage,
})

const adminAppsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/apps',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminAppsPage,
})

const adminAppReviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/app-review',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminAppReviewPage,
})

const adminAppTagsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/app-tags',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminAppTagsPage,
})

const adminAgentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/agent',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/agent/hub' })
  },
})

const adminHubAgentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/agent/hub',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminCoreAgentSettingsPage,
})

const adminMcpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/agent/mcp',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: lazyRouteComponent(
    () => import('@/features/admin/agent-tools/mcp-connections-page'),
    'MCPConnectionsPage',
  ),
})

const legacyAdminAgentToolsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/agent-tools',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/agent/mcp' })
  },
})

const adminModelProviderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/model-provider',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminModelProviderPage,
})

const adminModelKeysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/model-keys',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminModelKeysPage,
})

const adminModelCallLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/model-call-logs',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminModelCallLogsPage,
})

const adminStatisticsDashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/statistics/dashboard',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: lazyRouteComponent(
    () => import('@/features/admin/statistics/statistics-dashboard-page'),
    'AdminStatisticsDashboardPage',
  ),
})

const adminStatisticsConversationLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/statistics/conversations',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: lazyRouteComponent(
    () => import('@/features/admin/statistics/conversation-logs-page'),
    'AdminStatisticsConversationLogsPage',
  ),
})

const adminOrganizationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/organization/departments',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminOrganizationDepartmentsPage,
})

const adminMemberAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/members',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminMemberAccountsPage,
})

const adminOrganizationSyncRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/organization/sync',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminOrganizationSyncSettingsPage,
})

const adminSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/settings/admins' })
  },
})

const adminAccountsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/admins',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminAccountsPage,
})

const legacyAdminCoreAgentSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/core-agent',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/agent/hub' })
  },
})

const adminCredentialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/credentials',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/credentials/adp' })
  },
})

const adminAdpCredentialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/credentials/adp',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminAdpCredentialsPage,
})

const adminWecomIdentityProfilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/credentials/wecom',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminWecomIdentityProfilesPage,
})

const adminSsoSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/sso',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminSsoSettingsPage,
})

const adminIdpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/idp',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/idp/provider' })
  },
})

const adminIdpProviderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/idp/provider',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminIdpSettingsPage,
})

const adminIdpClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/idp/clients',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminOidcClientsPage,
})

const adminIdpResourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/idp/resources',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminOAuthResourcesPage,
})

const adminIdpClientDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/idp/clients/$clientRecordId',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminOidcClientDetailPage,
})

const adminIdpClientScopesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/idp/client-scopes',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminOidcClientScopesPage,
})

const adminIdpClientScopeDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/idp/client-scopes/$clientScopeId',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminOidcClientScopeDetailPage,
})

const legacyAdminIdpSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/idp',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/idp/provider' })
  },
})

const legacyAdminIdpClientScopesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/idp/client-scopes',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/idp/client-scopes' })
  },
})

const adminBrandingSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/branding',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminBrandingSettingsPage,
})

const adminBusinessNavigationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/business-navigation',
  beforeLoad: () => {
    requirePortalSession('admin')
  },
  component: AdminBusinessNavigationPage,
})

const legacyAdminAdpCredentialsSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/adp-credentials',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/credentials/adp' })
  },
})

const legacyAdminWecomIdentityProfilesSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/wecom-identity-profiles',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/credentials/wecom' })
  },
})

const legacyAdminMcpRuntimeSettingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/settings/mcp',
  beforeLoad: () => {
    requirePortalSession('admin')
    redirect({ throw: true, to: '/admin/agent/mcp' })
  },
})

const adminPlaceholderPaths = ['/admin/app-evals', '/admin/roles'] as const

const adminPlaceholderRoutes = adminPlaceholderPaths.map((path) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: () => {
      requirePortalSession('admin')
    },
    component: () => <AdminShellPage activePath={path} />,
  }),
)

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  passwordLoginRoute,
  ssoCallbackRoute,
  oauthAuthorizationResumeRoute,
  wecomAuthRoute,
  adminRoute,
  adminLoginRoute,
  userPasswordResetRoute,
  adminPasswordResetRoute,
  appsRoute,
  chatRoute,
  profileRoute,
  wecomBindingRoute,
  ...userPlaceholderRoutes,
  adminOverviewRoute,
  adminProfileRoute,
  adminAppsRoute,
  adminAppReviewRoute,
  adminAppTagsRoute,
  adminAgentRoute,
  adminHubAgentRoute,
  adminMcpRoute,
  legacyAdminAgentToolsRoute,
  adminModelProviderRoute,
  adminModelKeysRoute,
  adminModelCallLogsRoute,
  adminStatisticsDashboardRoute,
  adminStatisticsConversationLogsRoute,
  adminOrganizationRoute,
  adminMemberAccountsRoute,
  adminOrganizationSyncRoute,
  adminSettingsRoute,
  adminAccountsRoute,
  adminSsoSettingsRoute,
  adminIdpRoute,
  adminIdpProviderRoute,
  adminIdpResourcesRoute,
  adminIdpClientsRoute,
  adminIdpClientDetailRoute,
  adminIdpClientScopesRoute,
  adminIdpClientScopeDetailRoute,
  legacyAdminIdpSettingsRoute,
  legacyAdminIdpClientScopesRoute,
  legacyAdminCoreAgentSettingsRoute,
  adminCredentialsRoute,
  adminAdpCredentialsRoute,
  adminWecomIdentityProfilesRoute,
  legacyAdminAdpCredentialsSettingsRoute,
  legacyAdminWecomIdentityProfilesSettingsRoute,
  adminBrandingSettingsRoute,
  adminBusinessNavigationRoute,
  legacyAdminMcpRuntimeSettingsRoute,
  ...adminPlaceholderRoutes,
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
